
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { AuthHeroPanel } from "./auth-hero-panel";

export interface SignInPageProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  heroVariant?: "signin" | "signup";
  email?: string;
  password?: string;
  onEmailChange?: (value: string) => void;
  onPasswordChange?: (value: string) => void;
  onSignIn?: (event: React.FormEvent<HTMLFormElement>) => void;

  onResetPassword?: () => void;
  onCreateAccount?: () => void;
  isLoading?: boolean;
  error?: string;
  submitLabel?: string;
  headerContent?: React.ReactNode;
  children?: React.ReactNode;
}

const GlassInputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 transition-colors focus-within:border-zinc-400 focus-within:bg-white">
    {children}
  </div>
);

export const SignInPage: React.FC<SignInPageProps> = ({
  title = <span className="font-light tracking-tighter">Welcome</span>,
  description = "Access your account and continue your journey with us",
  heroVariant = "signin",
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSignIn,

  onResetPassword,
  onCreateAccount,
  isLoading = false,
  error,
  submitLabel = "Sign In",
  headerContent,
  children,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="min-h-[100dvh] flex flex-col md:flex-row w-full bg-white safe-area-top">
      {/* Left column */}
      <section className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex flex-col gap-6">
            <h1 className="animate-element animate-delay-100 text-4xl md:text-5xl font-semibold leading-tight text-zinc-900">
              {title}
            </h1>
            <p className="animate-element animate-delay-200 text-zinc-500">
              {description}
            </p>

            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
                {error}
              </div>
            )}

            <form className="space-y-5" onSubmit={onSignIn}>
              {headerContent}
              <div className="animate-element animate-delay-300">
                <label className="text-sm font-medium text-zinc-500 block mb-1.5">
                  Email Address
                </label>
                <GlassInputWrapper>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="email"
                    disabled={isLoading}
                    value={email}
                    onChange={(e) => onEmailChange?.(e.target.value)}
                    className="w-full bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 p-4 rounded-2xl focus:outline-none disabled:opacity-50"
                  />
                </GlassInputWrapper>
              </div>

              <div className="animate-element animate-delay-400">
                <label className="text-sm font-medium text-zinc-500 block mb-1.5">
                  Password
                </label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      disabled={isLoading}
                      value={password}
                      onChange={(e) => onPasswordChange?.(e.target.value)}
                      className="w-full bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 p-4 pr-12 rounded-2xl focus:outline-none disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword
                        ? <EyeOff className="w-5 h-5 text-zinc-400 hover:text-zinc-600 transition-colors" />
                        : <Eye className="w-5 h-5 text-zinc-400 hover:text-zinc-600 transition-colors" />}
                    </button>
                  </div>
                </GlassInputWrapper>
              </div>

              <div className="animate-element animate-delay-500 flex items-center justify-between text-sm">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" name="rememberMe" className="custom-checkbox" />
                  <span className="text-zinc-700">Keep me signed in</span>
                </label>
                <button
                  type="button"
                  onClick={() => onResetPassword?.()}
                  className="text-zinc-400 hover:text-zinc-700 transition-colors text-sm"
                >
                  Reset password
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="animate-element animate-delay-600 w-full rounded-2xl bg-zinc-900 py-4 font-medium text-white hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in…
                  </>
                ) : submitLabel}
              </button>
            </form>

            {children && (
              <div className="animate-element animate-delay-800">{children}</div>
            )}

            {onCreateAccount && (
              <p className="animate-element animate-delay-900 text-center text-sm text-zinc-500">
                New to our platform?{" "}
                <button onClick={onCreateAccount} className="text-zinc-900 font-medium hover:underline transition-colors">
                  Create Account
                </button>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Right column — cinematic panel */}
      <AuthHeroPanel variant={heroVariant} />
    </div>
  );
};
